import {
  Button,
  Cell,
  LoadingSpinner,
  TextInput,
} from '@zeiss/beyond-online-react';
import { Box, ButtonGroup, Grid, PageHeader } from '@zeiss/lib-components';
import { useBreakpoint } from '@zeiss/lib-helpers';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { isTenantValid, TenantCreateActions, TenantState } from './';
import {
  GLOBAL_ACTIONS,
  GLOBAL_RESOURCES,
  MAX_CHARS_FOR_TENANT_NAME,
} from 'src/constants';
import { showToast } from 'src/helpers/showToast';
import { useGlobalEvaluations, useInstanceEvaluations } from 'src/hooks';
import { useCreateTenant } from 'src/hooks/api/useCreateTenant';
import { OrganizationSearch } from 'src/modules/TenantManagement/components/OrganizationSearch';
import { StandardRouteParams } from 'src/types';
import { ManagementPolicy, OrganizationResponse } from 'src/types/rest/tenant';
import { ProblemDetails } from 'src/types/rest/tenant/schema';

const initialState: TenantState = {
  managementPolicy: ManagementPolicy.zeissManaged,
  managers: [],
  name: '',
  organization: null,
};

export const TenantCreate = () => {
  const { t } = useTranslation(['common']);
  const { applicationListId } = useParams<StandardRouteParams>();
  const navigate = useNavigate();
  const isMinTablet = useBreakpoint('tablet');

  const [tenant, setTenant] = React.useState<TenantState>(initialState);

  const { can: canGlobally } = useGlobalEvaluations();

  const { isError: isErrorEvaluations, isPending: isPendingEvaluations } =
    useInstanceEvaluations({
      actions: [...Object.values(TenantCreateActions)],
      requiredActions: [TenantCreateActions.CREATE_TENANT],
      resource: {
        id: applicationListId!,
        type: GLOBAL_RESOURCES.TM_APPLICATION_LIST,
      },
    });

  const { isPending: isCreateTenantPending, mutate: createTenant } =
    useCreateTenant();

  const canReadOrganizations = canGlobally({
    action: GLOBAL_ACTIONS.ORGS_ORGANIZATION_LIST,
    resource: GLOBAL_RESOURCES.ORGS_ORGANIZATION,
  });

  if (isPendingEvaluations) {
    return (
      <LoadingSpinner label={t('common:loadingFeedback.loading')} size="xl" />
    );
  }

  if (isErrorEvaluations) {
    return <div>Error loading data</div>;
  }

  const handleTenantOrganization = (
    organization: OrganizationResponse | null,
  ) => {
    setTenant((prev) => ({
      ...prev,
      organization,
    }));
  };

  const handleTenantNameChange: React.ChangeEventHandler<HTMLInputElement> = ({
    target: { value: name },
  }) => {
    setTenant((prev) => ({
      ...prev,
      error: '',
      name,
    }));
  };

  const cancel = () => {
    navigate('..', { relative: 'path' });
  };

  const save = () => {
    if (applicationListId) {
      const { managementPolicy, managers, name, organization } = tenant;
      const { customerNumber } = organization ?? {};

      createTenant(
        {
          applicationListId,
          managementPolicy: managementPolicy as ManagementPolicy,
          managerIds: managers.map(({ id }) => id),
          name,
          organizationCustomerNumber: customerNumber,
        },
        {
          onError: async (error) => {
            if (error instanceof Response && error.status === 400) {
              const problemDetails: ProblemDetails = await error.json();
              const errors = problemDetails.errors as Record<string, string[]>;
              const nameErrors = errors['Name'];

              if (
                nameErrors?.includes(
                  'Tenant name must be unique within the Application List',
                )
              ) {
                return setTenant((prev) => ({
                  ...prev,
                  error: t('error.tenantExists'),
                }));
              }
            }

            showToast({
              props: {
                title: t('common:notification.errorX', {
                  action: t('common:action.created'),
                  x: t('common:glossary.tenant'),
                }),
                type: 'error',
              },
            });
          },
          onSuccess: ({ id }) => {
            showToast({
              props: {
                title: t('common:notification.successX', {
                  action: t('common:action.created'),
                  x: t('common:glossary.tenant'),
                }),
                type: 'success',
              },
            });
            navigate(`../${id}`, { relative: 'path' });
          },
        },
      );
    }
  };

  const { error, name, organization } = tenant;

  return (
    <>
      <PageHeader
        headline={t('common:action.newX', {
          x: t('common:glossary.tenant'),
        })}
      />
      <Box spacing={isMinTablet ? 'm' : 'xl'}>
        <Grid>
          <Cell.Basic colSpan={[4, 4, 4]}>
            <TextInput
              autoComplete="off"
              error={error ? error : undefined}
              helperText={t('common:input.tenantNameHelperText')}
              label={t('common:label.xName', {
                x: t('common:glossary.tenant'),
              })}
              maxLength={MAX_CHARS_FOR_TENANT_NAME}
              onChange={handleTenantNameChange}
              value={name}
            />
          </Cell.Basic>
          {canReadOrganizations ? (
            <Cell.Basic colSpan={[4, 4, 4]} data-testid="organizationCell">
              <OrganizationSearch
                handleOrganizationSelect={handleTenantOrganization}
                selectedOrganization={organization}
              />
            </Cell.Basic>
          ) : null}
        </Grid>
      </Box>

      <Box>
        <ButtonGroup spacing="s" variant="form">
          <Button onClick={cancel} variant="secondary">
            {t('common:action.cancel')}
          </Button>

          <Button
            disabled={!isTenantValid(tenant)}
            loading={isCreateTenantPending}
            onClick={save}
          >
            {t('common:action.save')}
          </Button>
        </ButtonGroup>
      </Box>
    </>
  );
};
